import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';

export default function PostIngestionValidation() {
  const [report, setReport] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);
  const [ingestionSummary, setIngestionSummary] = useState(null);

  // Check for existing report
  useEffect(() => {
    apiFetch(`/validation/report`)
      .then(r => r.json())
      .then(data => { if (data.exists) setReport(data); })
      .catch(() => {});
  }, []);

  // Load step 4 ingestion summary — source side is always the most recently generated file
  useEffect(() => {
    apiFetch(`/ingestion/summary`)
      .then(r => r.json())
      .then(data => { if (data.exists) setIngestionSummary(data); })
      .catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiFetch(`/validation/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setUploadedFile(data.filename);
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  const handleValidate = async () => {
    setValidating(true);
    setError(null);
    try {
      const res = await apiFetch(`/validation/run`, { method: 'POST' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setReport(data);
    } catch (err) {
      setError(err.message);
    }
    setValidating(false);
  };

  // Derive a human-readable filename and date from the ingestion summary
  const ingestionDate = ingestionSummary?.generated_at
    ? ingestionSummary.generated_at.slice(0, 10)
    : null;
  const ingestionFilename = ingestionDate
    ? `frontify_ingestion_${ingestionDate}.csv`
    : 'frontify_ingestion.csv';
  const ingestionDateLabel = ingestionDate
    ? new Date(ingestionDate + 'T00:00:00').toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  const bothReady = ingestionSummary?.exists && !!uploadedFile;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-800">Post-Ingestion Validation</h2>
        <p className="text-sm text-slate-500 mt-1">
          Verify that all assets from the ingestion file landed correctly in Frontify
        </p>
      </div>

      {/* Panel 1 — Source file (pre-loaded from step 4) */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Source file</h3>
        {ingestionSummary?.exists ? (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
            <span className="text-green-600 text-lg flex-shrink-0">✓</span>
            <div className="text-sm text-green-800">
              <span className="font-medium">{ingestionFilename}</span>
              {' · '}
              {ingestionSummary.total_valid?.toLocaleString()} valid assets
              {ingestionDateLabel && ` · Generated ${ingestionDateLabel}`}
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            No ingestion file found. Complete step 4 (Export) before running validation.
          </div>
        )}
      </div>

      {/* Panel 2 — Frontify export upload */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Upload Frontify metadata export</h3>
        <div className="text-sm text-slate-600 mb-4 leading-relaxed">
          <strong>How to get this file:</strong> In Frontify, open your Library → click the ··· menu
          → <strong>Export Metadata</strong>. This downloads a CSV of all assets currently in the
          library. Upload that file here to verify all assets from the ingestion landed correctly.
        </div>
        <div className="flex items-center gap-4">
          <label className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 cursor-pointer transition">
            {uploading ? 'Uploading...' : 'Upload Frontify export CSV'}
            <input type="file" accept=".csv" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
          {uploadedFile && (
            <span className="text-sm text-green-700 bg-green-50 px-3 py-1 rounded">
              ✓ {uploadedFile}
            </span>
          )}
        </div>
      </div>

      {/* Panel 3 — Run Validation */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Run Validation</h3>
        <div className="space-y-2 mb-5">
          <div className="flex items-center gap-2 text-sm">
            <span className={ingestionSummary?.exists ? 'text-green-600' : 'text-red-500'}>
              {ingestionSummary?.exists ? '✓' : '✗'}
            </span>
            <span className="text-slate-700">
              Source: Ingestion file loaded
              {ingestionSummary?.exists && ` (${ingestionSummary.total_valid?.toLocaleString()} assets)`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className={uploadedFile ? 'text-green-600' : 'text-red-500'}>
              {uploadedFile ? '✓' : '✗'}
            </span>
            <span className="text-slate-700">Target: Frontify export uploaded</span>
          </div>
        </div>
        <button
          onClick={handleValidate}
          disabled={!bothReady || validating}
          className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition"
        >
          {validating ? 'Validating...' : 'Compare Against Ingestion File'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">{error}</div>
      )}

      {report && <ValidationReport report={report} />}
    </div>
  );
}


function ValidationReport({ report }) {
  const { overall_status, count_comparison, asset_diff, column_comparison, field_comparison } = report;
  const isPass = overall_status === 'pass';

  return (
    <div>
      {/* Overall status banner */}
      <div className={`rounded-lg p-6 mb-6 ${isPass ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-start gap-3">
          <div className={`text-xl ${isPass ? 'text-green-600' : 'text-amber-600'}`}>
            {isPass ? '✓' : '⚠'}
          </div>
          <div>
            <div className={`text-sm font-semibold ${isPass ? 'text-green-800' : 'text-amber-800'}`}>
              {isPass ? 'Migration validated successfully' : 'Discrepancies found — review below'}
            </div>
            <div className={`text-sm mt-1 ${isPass ? 'text-green-700' : 'text-amber-700'}`}>
              {isPass
                ? `All ${count_comparison.expected} assets matched between the ingestion file and Frontify.`
                : `Expected ${count_comparison.expected} assets, found ${count_comparison.actual} in Frontify.`
              }
            </div>
          </div>
        </div>
      </div>

      {/* Count comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <ComparisonCard
          label="Expected (sent)"
          value={count_comparison.expected}
          status="neutral"
        />
        <ComparisonCard
          label="Actual (in Frontify)"
          value={count_comparison.actual}
          status={count_comparison.match ? 'pass' : 'fail'}
        />
        <ComparisonCard
          label="Matched by ID"
          value={asset_diff?.matched || 0}
          status={asset_diff?.matched === count_comparison.expected ? 'pass' : 'fail'}
        />
      </div>

      {field_comparison && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-baseline mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Metadata Fidelity</h3>
            <span className={`text-xs font-medium ${field_comparison.total_mismatches === 0 ? 'text-green-700' : 'text-red-600'}`}>
              {field_comparison.total_mismatches === 0
                ? `${field_comparison.total_values_compared.toLocaleString()} values matched`
                : `${field_comparison.total_mismatches.toLocaleString()} mismatches`}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Compares every returned Frontify field that has the same name as a field in the ingestion package.
          </p>
          <div className="space-y-2">
            {(field_comparison.fields || []).map(field => (
              <div key={field.field} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                <span className="text-slate-700">{field.field}</span>
                <span className={field.mismatches === 0 ? 'text-green-700' : 'text-red-600'}>
                  {field.mismatches === 0
                    ? `${field.compared.toLocaleString()} matched`
                    : `${field.mismatches.toLocaleString()} of ${field.compared.toLocaleString()} differ`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missing assets */}
      {asset_diff?.missing_from_frontify?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-baseline mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Assets Missing from Frontify</h3>
            <span className="text-xs text-red-600 font-medium">
              {(asset_diff.missing_count ?? asset_diff.missing_from_frontify.length).toLocaleString()} asset{(asset_diff.missing_count ?? asset_diff.missing_from_frontify.length) > 1 ? 's' : ''}
            </span>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            These assets were in the ingestion file but were not found in the Frontify export.
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Asset ID</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Filename</th>
              </tr>
            </thead>
            <tbody>
              {asset_diff.missing_from_frontify.map(item => (
                <tr key={item.id} className="border-b border-slate-100">
                  <td className="py-2 px-3 text-slate-600 font-mono text-xs">{item.id}</td>
                  <td className="py-2 px-3 text-slate-800">{item.filename}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Extra assets in Frontify */}
      {asset_diff?.extra_in_frontify?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-baseline mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Extra Assets in Frontify</h3>
            <span className="text-xs text-amber-600 font-medium">
              {(asset_diff.extra_count ?? asset_diff.extra_in_frontify.length).toLocaleString()} asset{(asset_diff.extra_count ?? asset_diff.extra_in_frontify.length) > 1 ? 's' : ''}
            </span>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            These assets were found in Frontify but were not in the ingestion file.
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Asset ID</th>
              </tr>
            </thead>
            <tbody>
              {asset_diff.extra_in_frontify.map(item => (
                <tr key={item.id} className="border-b border-slate-100">
                  <td className="py-2 px-3 text-slate-600 font-mono text-xs">{item.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Column comparison */}
      {column_comparison?.extra_in_frontify?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Additional Frontify Columns</h3>
          <div className="text-xs text-slate-500 mb-2">
            These columns appear in the Frontify export but were not in the ingestion file. This is normal — Frontify adds system fields automatically.
          </div>
          <div className="flex flex-wrap gap-2">
            {column_comparison.extra_in_frontify.map(col => (
              <span key={col} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">{col}</span>
            ))}
          </div>
        </div>
      )}

      {/* Report files */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">QC Report Files</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded text-sm">
            <span className="text-slate-400">📄</span>
            <div>
              <div className="font-medium text-slate-800">Validation Report (CSV)</div>
              <div className="text-xs text-slate-500">output/validation_report.csv — for client sign-off</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded text-sm">
            <span className="text-slate-400">📄</span>
            <div>
              <div className="font-medium text-slate-800">Validation Report (JSON)</div>
              <div className="text-xs text-slate-500">output/validation_report.json — detailed machine-readable report</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function ComparisonCard({ label, value, status }) {
  const borderColors = {
    pass: 'border-green-500 bg-green-50',
    fail: 'border-red-500 bg-red-50',
    neutral: 'border-slate-400 bg-slate-50',
  };
  return (
    <div className={`bg-white rounded-lg shadow-sm border-l-4 p-4 ${borderColors[status] || borderColors.neutral}`}>
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-800 mt-1">{value?.toLocaleString()}</div>
    </div>
  );
}
